import {
    CheckCircleOutlined,
    CloudUploadOutlined,
    EyeOutlined,
    RedoOutlined,
    ReloadOutlined,
    SendOutlined,
} from '@ant-design/icons';
import {
    Alert,
    Button,
    type ButtonProps,
    DatePicker,
    Descriptions,
    Input,
    Modal,
    Pagination,
    Popconfirm,
    Select,
    Space,
    Spin,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import WebGrid, { type WebGridColumn } from '../../../webgrid/src';
import {
    apiErrorKey,
    buildSubmissionQuery,
    describeSubmissionError,
    E_INVOICE_RUN_PATH,
    E_INVOICE_PAGE_SIZE,
    E_INVOICE_STATUSES,
    EMPTY_SUBMISSION_FILTER,
    type EInvoiceStatus,
    type EInvoiceSubmission,
    type EInvoiceSummary,
    formatInvoiceAmount,
    formatPayload,
    operationLabelKey,
    type QueueRow,
    runNowBlockers,
    statusColor,
    statusCounts,
    statusLabelKey,
    type SubmissionFilter,
    submissionActions,
    type SubmissionListResponse,
    toQueueRow,
} from '../../domain/eInvoice';
import useRequest from '../../hooks/useRequest';

const { Text } = Typography;

const DATE_FORMAT = 'YYYY-MM-DD';
const GRID_HEIGHT = 'calc(100vh - 420px)';
// Its own key, so widths resized on another untitled grid are not applied here.
const GRID_STORAGE_KEY = 'webgrid-column-widths-e-invoice-queue';

interface EInvoiceQueueTabProps {
    isAdmin: boolean;
    summary: EInvoiceSummary | undefined;
    onChanged: () => void;
    /** A run is sending now: nobody can start another. */
    runActive: boolean;
    /** A run was just started here: read its progress at once. */
    onRunStarted: () => void;
    /** Changes when a run ends, so the rows are read again. */
    reloadKey: number;
}

/** The reason an administrator gives for a manual resolution or a drift acknowledgement. */
type ReasonDialog = { kind: 'markHandled'; row: QueueRow } | { kind: 'acknowledgeDrift' };

const renderDash = (value: unknown) => (value as string | null) ?? '-';

/** A text a narrow column may cut short, readable whole on hover. */
const renderTitled = (value: unknown) => {
    const text = (value as string | null) ?? '-';

    return <span title={text}>{text}</span>;
};

function formatTimestamp(value: string | null | undefined): string {
    return value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-';
}

function StatusTag({ status }: Readonly<{ status: EInvoiceStatus }>) {
    const { t } = useTranslation();

    return (
        <Tag color={statusColor(status)} data-testid={`e-invoice-status-${status}`}>
            {t(statusLabelKey(status))}
        </Tag>
    );
}

function ErrorText({ row }: Readonly<{ row: Pick<QueueRow, 'errorCode' | 'errorMessage'> }>) {
    const { t } = useTranslation();
    const description = describeSubmissionError(row);

    if (description === null) {
        return <>-</>;
    }

    const text = description.kind === 'key' ? t(description.key) : description.text;

    return <span title={text}>{text}</span>;
}

/** A row action as a small icon button, named by its tooltip, so the actions column stays narrow. */
function RowAction({
    label,
    icon,
    testId,
    ...buttonProps
}: Readonly<{ label: string; icon: ReactNode; testId: string } & ButtonProps>) {
    return (
        <Tooltip title={label}>
            <Button
                {...buttonProps}
                size="small"
                icon={icon}
                aria-label={label}
                data-testid={testId}
            />
        </Tooltip>
    );
}

/** Why "Send unsent invoices now" is disabled, one reason per line. */
function RunBlockedReasons({ reasons }: Readonly<{ reasons: string[] }>) {
    const { t } = useTranslation();

    return (
        <div data-testid="e-invoice-run-now-blocked">
            {reasons.map(reason => (
                <div key={reason}>{t(reason)}</div>
            ))}
        </div>
    );
}

function PayloadBlock({ label, payload }: Readonly<{ label: string; payload: string }>) {
    return (
        <div style={{ marginTop: 12 }}>
            <Text strong>{label}</Text>
            <pre
                style={{
                    maxHeight: 280,
                    overflow: 'auto',
                    background: 'rgba(0, 0, 0, 0.04)',
                    padding: 8,
                    marginTop: 4,
                }}
            >
                {payload === '' ? '-' : payload}
            </pre>
        </div>
    );
}

/**
 * The queue and history of e-invoice submissions (plan §7.2, tab 1), with the drift banner and,
 * for administrators, the manual actions the backend allows on each row.
 */
export default function EInvoiceQueueTab({
    isAdmin,
    summary,
    onChanged,
    runActive,
    onRunStarted,
    reloadKey,
}: Readonly<EInvoiceQueueTabProps>) {
    const { t } = useTranslation();
    const request = useRequest();
    const [filter, setFilter] = useState<SubmissionFilter>(EMPTY_SUBMISSION_FILTER);
    const [searchText, setSearchText] = useState('');
    const [rows, setRows] = useState<QueueRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [detail, setDetail] = useState<EInvoiceSubmission | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [reasonDialog, setReasonDialog] = useState<ReasonDialog | null>(null);
    const [reason, setReason] = useState('');
    const [reasonSaving, setReasonSaving] = useState(false);
    const [starting, setStarting] = useState(false);

    const loadRows = useCallback(async () => {
        setLoading(true);
        try {
            const result = await request<SubmissionListResponse>({
                path: '/e-invoice/submissions',
                params: buildSubmissionQuery(filter),
            });
            setRows(result.data.map(toQueueRow));
            setTotal(result.total);
        } catch (error) {
            console.error('Loading e-invoice submissions failed:', error);
            message.error(t(apiErrorKey(error, 'eInvoice.queue.loadFailed')));
        } finally {
            setLoading(false);
        }
    }, [filter, request, t]);

    useEffect(() => {
        void loadRows();
    }, [loadRows, reloadKey]);

    const refresh = useCallback(() => {
        void loadRows();
        onChanged();
    }, [loadRows, onChanged]);

    const runAction = useCallback(
        async (row: QueueRow, path: string, successKey: string) => {
            setBusyId(row.id);
            try {
                await request({ path, method: 'POST' });
                message.success(t(successKey, { number: row.invoiceNumber }));
            } catch (error) {
                console.error('E-invoice action failed:', error);
                message.error(t(apiErrorKey(error, 'eInvoice.queue.actionFailed')));
            } finally {
                setBusyId(null);
                refresh();
            }
        },
        [refresh, request, t],
    );

    const openDetail = useCallback(
        async (row: QueueRow) => {
            setDetail(row.submission);
            setDetailLoading(true);
            try {
                const result = await request<{ data: EInvoiceSubmission }>({
                    path: `/e-invoice/submissions/${row.id}`,
                });
                setDetail(result.data);
            } catch (error) {
                console.error('Loading an e-invoice submission failed:', error);
                message.error(t(apiErrorKey(error, 'eInvoice.queue.loadFailed')));
            } finally {
                setDetailLoading(false);
            }
        },
        [request, t],
    );

    /** Send every invoice not sent yet, as the handover question does; the progress shows above. */
    const startRun = async () => {
        setStarting(true);
        try {
            await request({ path: E_INVOICE_RUN_PATH, method: 'POST', params: { source: 'PAGE' } });
            message.success(t('eInvoice.run.startedHere'));
        } catch (error) {
            console.error('Starting the e-invoice run failed:', error);
            message.error(t(apiErrorKey(error, 'eInvoice.run.failed')));
        } finally {
            setStarting(false);
            onRunStarted();
            refresh();
        }
    };

    const closeReasonDialog = () => {
        setReasonDialog(null);
        setReason('');
    };

    const saveReason = async () => {
        if (reasonDialog === null) {
            return;
        }

        const path =
            reasonDialog.kind === 'markHandled'
                ? `/e-invoice/submissions/${reasonDialog.row.id}/mark-handled`
                : '/e-invoice/drift/acknowledge';

        setReasonSaving(true);
        try {
            await request({ path, method: 'POST', params: { reason } });
            message.success(t(`eInvoice.queue.${reasonDialog.kind}Done`));
            closeReasonDialog();
            refresh();
        } catch (error) {
            console.error('E-invoice resolution failed:', error);
            message.error(t(apiErrorKey(error, 'eInvoice.queue.actionFailed')));
        } finally {
            setReasonSaving(false);
        }
    };

    // Widths sum to about 1050px, so the grid fits beside the sidebar on a 1366px screen; a
    // wider screen spreads the rest over the columns. Long texts are cut short, whole on hover.
    const columns = useMemo<WebGridColumn<QueueRow>[]>(() => {
        const base: WebGridColumn<QueueRow>[] = [
            {
                key: 'invoiceNumber',
                title: t('eInvoice.queue.invoiceNumber'),
                dataIndex: 'invoiceNumber',
                sortable: true,
                width: 85,
            },
            {
                key: 'invoiceDate',
                title: t('eInvoice.queue.invoiceDate'),
                dataIndex: 'invoiceDate',
                sortable: true,
                width: 85,
                render: renderDash,
            },
            {
                key: 'invoiceType',
                title: t('eInvoice.queue.invoiceType'),
                dataIndex: 'invoiceType',
                width: 65,
                render: renderTitled,
            },
            {
                key: 'clientName',
                title: t('eInvoice.queue.client'),
                dataIndex: 'clientName',
                width: 115,
                render: renderTitled,
            },
            {
                key: 'amount',
                title: t('eInvoice.queue.amount'),
                dataIndex: 'amount',
                align: 'right',
                width: 75,
                render: (value: unknown) => formatInvoiceAmount(value as number | null),
            },
            {
                key: 'operation',
                title: t('eInvoice.queue.operation'),
                dataIndex: 'operation',
                width: 70,
                render: (_value: unknown, row: QueueRow) => t(operationLabelKey(row.operation)),
            },
            {
                key: 'status',
                title: t('eInvoice.queue.status'),
                dataIndex: 'status',
                width: 90,
                render: (_value: unknown, row: QueueRow) => <StatusTag status={row.status} />,
            },
            {
                key: 'providerReference',
                title: t('eInvoice.queue.providerNumber'),
                dataIndex: 'providerReference',
                width: 90,
                render: renderDash,
            },
            {
                key: 'error',
                title: t('eInvoice.queue.error'),
                dataIndex: 'errorMessage',
                width: 115,
                render: (_value: unknown, row: QueueRow) => <ErrorText row={row} />,
            },
            {
                key: 'attempts',
                title: t('eInvoice.queue.attempts'),
                dataIndex: 'attempts',
                align: 'right',
                width: 55,
            },
            {
                key: 'lastAttemptAt',
                title: t('eInvoice.queue.lastAttempt'),
                dataIndex: 'lastAttemptAt',
                width: 110,
                render: (value: unknown) => formatTimestamp(value as string | null),
            },
        ];

        const actions: WebGridColumn<QueueRow> = {
            key: 'actions',
            title: t('eInvoice.queue.actions'),
            dataIndex: 'id',
            width: 95,
            render: (_value: unknown, row: QueueRow) => {
                const allowed = submissionActions(row, isAdmin);
                const busy = busyId === row.id;

                return (
                    <Space size={4}>
                        <RowAction
                            label={t('eInvoice.queue.details')}
                            icon={<EyeOutlined />}
                            testId={`e-invoice-details-${row.id}`}
                            onClick={() => void openDetail(row)}
                        />
                        {allowed.sendNow && (
                            <RowAction
                                label={t('eInvoice.queue.sendNow')}
                                icon={<SendOutlined />}
                                testId={`e-invoice-send-${row.id}`}
                                type="primary"
                                loading={busy}
                                onClick={() =>
                                    void runAction(
                                        row,
                                        `/e-invoice/invoices/${row.invoiceId}/send`,
                                        'eInvoice.queue.sent',
                                    )
                                }
                            />
                        )}
                        {allowed.retry && (
                            <RowAction
                                label={t('eInvoice.queue.retry')}
                                icon={<RedoOutlined />}
                                testId={`e-invoice-retry-${row.id}`}
                                loading={busy}
                                onClick={() =>
                                    void runAction(
                                        row,
                                        `/e-invoice/submissions/${row.id}/retry`,
                                        'eInvoice.queue.retried',
                                    )
                                }
                            />
                        )}
                        {allowed.markHandled && (
                            <RowAction
                                label={t('eInvoice.queue.markHandled')}
                                icon={<CheckCircleOutlined />}
                                testId={`e-invoice-mark-handled-${row.id}`}
                                disabled={busy}
                                onClick={() => setReasonDialog({ kind: 'markHandled', row })}
                            />
                        )}
                    </Space>
                );
            },
        };

        return [...base, actions];
    }, [busyId, isAdmin, openDetail, runAction, t]);

    const drift = summary?.drift;
    const counts = summary ? statusCounts(summary.counts) : [];
    // Always shown; disabled, with the reasons on hover, whenever it would be refused.
    const runBlockers = runNowBlockers(summary, isAdmin, runActive);
    const runButton = (
        <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            loading={starting}
            disabled={runBlockers.length > 0}
            data-testid="e-invoice-run-now"
        >
            {t('eInvoice.run.button')}
        </Button>
    );
    const dateRange: [Dayjs | null, Dayjs | null] = [
        filter.from ? dayjs(filter.from) : null,
        filter.to ? dayjs(filter.to) : null,
    ];

    return (
        <div data-testid="e-invoice-queue">
            {drift?.drifted && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                    data-testid="e-invoice-drift-banner"
                    message={t('eInvoice.drift.title')}
                    description={t('eInvoice.drift.description', {
                        invoiceNumber: drift.invoiceNumber,
                        providerNumber: drift.providerNumber,
                    })}
                    action={
                        isAdmin && (
                            <Button
                                danger
                                onClick={() => setReasonDialog({ kind: 'acknowledgeDrift' })}
                                data-testid="e-invoice-drift-acknowledge"
                            >
                                {t('eInvoice.drift.acknowledge')}
                            </Button>
                        )
                    }
                />
            )}

            {summary && (
                <Space wrap style={{ marginBottom: 12 }} data-testid="e-invoice-summary">
                    {counts.length === 0 && (
                        <Text type="secondary">{t('eInvoice.summary.empty')}</Text>
                    )}
                    {counts.map(entry => (
                        <Tag
                            key={entry.status}
                            color={statusColor(entry.status)}
                            data-testid={`e-invoice-count-${entry.status}`}
                        >
                            {t(statusLabelKey(entry.status))}: {entry.count}
                        </Tag>
                    ))}
                    <Text type="secondary">
                        {t('eInvoice.summary.lastSubmitted', {
                            when: formatTimestamp(summary.lastSubmittedAt),
                        })}
                    </Text>
                    <Tag color={summary.autoSubmit ? 'green' : 'default'}>
                        {t(
                            summary.autoSubmit
                                ? 'eInvoice.summary.autoOn'
                                : 'eInvoice.summary.autoOff',
                        )}
                    </Tag>
                </Space>
            )}

            <Space wrap style={{ marginBottom: 12 }}>
                <Select<EInvoiceStatus>
                    allowClear
                    style={{ minWidth: 200 }}
                    placeholder={t('eInvoice.queue.allStatuses')}
                    value={filter.status ?? undefined}
                    onChange={value => setFilter({ ...filter, status: value ?? null, page: 1 })}
                    options={E_INVOICE_STATUSES.map(status => ({
                        value: status,
                        label: t(statusLabelKey(status)),
                    }))}
                    data-testid="e-invoice-status-filter"
                />
                <DatePicker.RangePicker
                    allowEmpty={[true, true]}
                    format={DATE_FORMAT}
                    value={dateRange}
                    onChange={values =>
                        setFilter({
                            ...filter,
                            from: values?.[0]?.format(DATE_FORMAT) ?? null,
                            to: values?.[1]?.format(DATE_FORMAT) ?? null,
                            page: 1,
                        })
                    }
                />
                <Input.Search
                    allowClear
                    style={{ width: 240 }}
                    placeholder={t('eInvoice.queue.searchPlaceholder')}
                    value={searchText}
                    onChange={event => setSearchText(event.target.value)}
                    onSearch={value => setFilter({ ...filter, search: value, page: 1 })}
                    data-testid="e-invoice-search"
                />
                <Button icon={<ReloadOutlined />} onClick={refresh} data-testid="e-invoice-refresh">
                    {t('eInvoice.queue.refresh')}
                </Button>
                {runBlockers.length > 0 ? (
                    <Tooltip title={<RunBlockedReasons reasons={runBlockers} />}>
                        {runButton}
                    </Tooltip>
                ) : (
                    <Popconfirm
                        title={t('eInvoice.run.confirm')}
                        description={t('eInvoice.run.confirmDescription')}
                        okText={t('common.yes')}
                        cancelText={t('common.no')}
                        onConfirm={() => void startRun()}
                    >
                        {runButton}
                    </Popconfirm>
                )}
            </Space>

            <WebGrid<QueueRow>
                data={rows}
                columns={columns}
                loading={loading}
                rowIdAccessor={row => row.id}
                height={GRID_HEIGHT}
                storageKey={GRID_STORAGE_KEY}
                emptyMessage={t('eInvoice.queue.empty')}
            />
            <Pagination
                style={{ marginTop: 8, textAlign: 'right' }}
                size="small"
                current={filter.page}
                pageSize={E_INVOICE_PAGE_SIZE}
                total={total}
                showSizeChanger={false}
                showTotal={count => t('eInvoice.queue.total', { count })}
                onChange={page => setFilter({ ...filter, page })}
            />

            <Modal
                open={detail !== null}
                title={t('eInvoice.detail.title', { number: detail?.invoiceNumber ?? '' })}
                width={900}
                onCancel={() => setDetail(null)}
                destroyOnHidden
                footer={<Button onClick={() => setDetail(null)}>{t('common.close')}</Button>}
            >
                <Spin spinning={detailLoading}>
                    {detail && (
                        <div data-testid="e-invoice-detail">
                            <Descriptions size="small" bordered column={{ xs: 1, md: 2 }}>
                                <Descriptions.Item label={t('eInvoice.queue.operation')}>
                                    {t(operationLabelKey(detail.operation))}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('eInvoice.queue.status')}>
                                    <StatusTag status={detail.status} />
                                </Descriptions.Item>
                                <Descriptions.Item label={t('eInvoice.queue.providerNumber')}>
                                    {toQueueRow(detail).providerReference ?? '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('eInvoice.queue.attempts')}>
                                    {detail.attempts}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('eInvoice.detail.submittedAt')}>
                                    {formatTimestamp(detail.submittedAt)}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('eInvoice.detail.nextAttemptAt')}>
                                    {formatTimestamp(detail.nextAttemptAt)}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('eInvoice.queue.error')} span={2}>
                                    <ErrorText row={detail} />
                                </Descriptions.Item>
                                <Descriptions.Item label={t('eInvoice.detail.resolution')} span={2}>
                                    {detail.resolutionNote ?? '-'}
                                </Descriptions.Item>
                                <Descriptions.Item
                                    label={t('eInvoice.detail.centralCopy')}
                                    span={2}
                                >
                                    {formatTimestamp(detail.centralCopiedAt)}
                                </Descriptions.Item>
                            </Descriptions>
                            {detail.payloadsHidden ? (
                                <Alert
                                    style={{ marginTop: 12 }}
                                    type="info"
                                    showIcon
                                    message={t('eInvoice.detail.payloadsHidden')}
                                />
                            ) : (
                                <>
                                    <PayloadBlock
                                        label={t('eInvoice.detail.request')}
                                        payload={formatPayload(detail.requestPayload)}
                                    />
                                    <PayloadBlock
                                        label={t('eInvoice.detail.response')}
                                        payload={formatPayload(detail.responseRaw)}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </Spin>
            </Modal>

            <Modal
                open={reasonDialog !== null}
                title={t(
                    reasonDialog?.kind === 'markHandled'
                        ? 'eInvoice.queue.markHandledTitle'
                        : 'eInvoice.drift.acknowledgeTitle',
                )}
                onCancel={closeReasonDialog}
                destroyOnHidden
                footer={[
                    <Button key="cancel" onClick={closeReasonDialog}>
                        {t('common.cancel')}
                    </Button>,
                    <Button
                        key="confirm"
                        type="primary"
                        disabled={reason.trim() === ''}
                        loading={reasonSaving}
                        onClick={() => void saveReason()}
                        data-testid="e-invoice-reason-confirm"
                    >
                        {t('common.confirm')}
                    </Button>,
                ]}
            >
                <Text type="secondary">
                    {t(
                        reasonDialog?.kind === 'markHandled'
                            ? 'eInvoice.queue.markHandledHint'
                            : 'eInvoice.drift.acknowledgeHint',
                    )}
                </Text>
                <Input.TextArea
                    style={{ marginTop: 8 }}
                    rows={3}
                    maxLength={500}
                    value={reason}
                    onChange={event => setReason(event.target.value)}
                    placeholder={t('eInvoice.queue.reasonPlaceholder')}
                    data-testid="e-invoice-reason"
                />
            </Modal>
        </div>
    );
}
